import { Suspense } from 'react';

import { getStateQueryConfig } from '@api/lendingAPI/queryConfigs';
import { UKOnly } from '@iwIntlProvider/CountryWrappers';
import { useQuery } from '@tanstack/react-query';
import { Card, H2, H3, Skeleton, Text } from '@ui';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { ErrorBoundary } from '../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import {
    ADD_PERSON,
    EDIT_PERSON
} from '../../modals/personModal/PersonModal.actions';
import { CreateNewCard } from '../cards/CreateNewCard';
import { PersonCard } from '../cards/PersonCard';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    addPerson: () => {
        dispatch({
            type: ADD_PERSON
        });
    },
    editPerson: (uid: string) => {
        dispatch({
            type: EDIT_PERSON,
            uid: uid
        });
    }
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

const People = ({ editPerson, addPerson }: PropsFromRedux) => {
    return (
        <div className="space-y-xl">
            <div>
                <H2>
                    <FormattedMessage
                        id="_.your_company.people.title"
                        defaultMessage="People"
                    />
                </H2>
                <Text className="mt-xs">
                    <FormattedMessage
                        id="_.your_company.people.subtitle"
                        defaultMessage="Add details of directors, shareholders or other people involved in your company"
                    />
                </Text>
            </div>

            <div className="space-y-m">
                <div>
                    <H3>
                        <FormattedMessage
                            id="_.your_company.directors_and_shareholders"
                            defaultMessage="Directors and Shareholders"
                        />
                    </H3>
                    <UKOnly>
                        <Text className="mt-xs">
                            <FormattedMessage
                                id="_.your_company.directors_and_shareholders_intro_text"
                                defaultMessage="We need details of all the directors and major shareholders with 25% or more of shares or voting rights in your business, as we're required to run anti-money laundering checks (don't worry, this won't affect anyone's credit score)."
                            />
                        </Text>
                    </UKOnly>
                </div>

                <div className="block grid-cols-2 gap-l space-y-m min-sm:grid min-sm:space-y-0 min-l:grid-cols-3">
                    <ErrorBoundary fallback={() => <CardSkeleton />}>
                        <Suspense fallback={<CardSkeleton />}>
                            <PeopleCards editPerson={editPerson} />
                        </Suspense>
                    </ErrorBoundary>

                    <CreateNewCard
                        onClick={addPerson}
                        label={
                            <FormattedMessage
                                id="_.your_company.add_another_person"
                                defaultMessage="Add another person"
                            />
                        }
                        data-testid="add-new-person"
                    />
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(People);

const PeopleCards = ({ editPerson }: { editPerson: (id: string) => void }) => {
    const stateKey = useStateKey();

    const { data: people } = useQuery({
        ...getStateQueryConfig(stateKey),
        select: (state) => state.data.application?.people,
        suspense: true
    });

    const sortedByApplicant = _.orderBy(
        people,
        (person) => person.roles?.includes('applicant'),
        'desc'
    );

    return (
        <>
            {sortedByApplicant?.map((person, key) => (
                <PersonCard
                    key={key}
                    firstName={person.first_name}
                    lastName={person.last_name}
                    roles={person.roles}
                    isGuarantor={person.roles?.includes('guarantor')}
                    onClick={() => editPerson(person.uid)}
                />
            ))}
        </>
    );
};

const CardSkeleton = () => {
    return (
        <Card data-testid="card-skeleton" className="space-y-s">
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
        </Card>
    );
};
